// src/components/character_editor/sections/SkillsEditSection.jsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  LinearProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Build as BuildIcon,
  Science as ScienceIcon,
  Groups as GroupsIcon,
  Info as InfoIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const SkillsEditSection = ({
  character,
  skills,
  selectedTemplate,
  selectedRace,
  onSkillsChange
}) => {
  const [skillDetails, setSkillDetails] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Ограничения
  const MAX_SKILLS = 7;
  const currentSkills = character.skills || [];
  const currentCount = currentSkills.length;
  const skillsRemaining = MAX_SKILLS - currentCount;
  const isAtLimit = currentCount >= MAX_SKILLS;

  // Обязательные навыки шаблона
  const requiredSkills = useMemo(() => 
    selectedTemplate?.primary_skills || [], 
    [selectedTemplate]
  );

  // Заблокированные навыки для расы
  const blockedSkills = useMemo(() => 
    selectedRace?.incompatible_skills || [], 
    [selectedRace]
  );

  // Группировка навыков по категориям
  const skillCategories = useMemo(() => {
    const categories = {};
    skills.forEach(skill => {
      const category = skill.category || 'other';
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(skill);
    });
    return categories;
  }, [skills]);

  // Функции для отображения
  const getCategoryIcon = (category) => {
    const icons = {
      technical: <BuildIcon />,
      science: <ScienceIcon />,
      scientific: <ScienceIcon />,
      social: <GroupsIcon />,
      combat: '⚔️',
      exploration: '🧭',
      survival: '🛡️',
      production: '🏭',
      creative: '🎨',
      other: '🔧'
    };
    return icons[category] || '🔧';
  };

  const getCategoryName = (category) => {
    const names = {
      technical: 'Технические',
      science: 'Научные',
      scientific: 'Научные',
      social: 'Социальные',
      combat: 'Боевые',
      exploration: 'Исследовательские',
      survival: 'Навыки выживания',
      production: 'Производственные',
      creative: 'Творческие',
      other: 'Прочие'
    };
    return names[category] || category;
  };

  // Проверки статуса навыка
  const isSkillSelected = (skillId) => currentSkills.includes(skillId);
  const isSkillRequired = (skillId) => requiredSkills.includes(skillId);
  const isSkillBlocked = (skillId) => blockedSkills.includes(skillId);
  
  const canSelectSkill = (skillId) => {
    if (isSkillBlocked(skillId)) return false;
    if (isSkillSelected(skillId)) return true;
    return !isAtLimit || isSkillRequired(skillId);
  };

  const getSkillStatus = (skill) => {
    if (isSkillBlocked(skill.id)) {
      return { type: 'blocked', label: 'Недоступно для расы', color: 'error' };
    }
    if (isSkillRequired(skill.id)) {
      return { type: 'required', label: 'Обязательно для шаблона', color: 'success' };
    }
    if (isSkillSelected(skill.id)) {
      return { type: 'selected', label: 'Выбрано', color: 'primary' };
    }
    return { type: 'available', label: 'Доступно', color: 'default' };
  };

  // Обработчики
  const handleSkillToggle = (skillId) => {
    if (!canSelectSkill(skillId)) return;

    let newSkills;
    if (isSkillSelected(skillId)) {
      // Не позволяем убирать обязательные навыки
      if (isSkillRequired(skillId)) return;
      newSkills = currentSkills.filter(id => id !== skillId);
    } else {
      newSkills = [...currentSkills, skillId];
    }

    onSkillsChange(newSkills);
  };

  const handleOpenDetails = (skill) => {
    setSkillDetails(skill);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSkillDetails(null);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BuildIcon sx={{ mr: 2 }} />
        Навыки и способности
      </Typography>

      {/* Статистика и прогресс */}
      <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Выбрано навыков: {currentCount} / {MAX_SKILLS}
            </Typography>
            <Chip 
              label={`Осталось слотов: ${skillsRemaining}`}
              color={skillsRemaining > 3 ? 'success' : skillsRemaining > 0 ? 'warning' : 'error'}
              variant="outlined"
            />
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={(currentCount / MAX_SKILLS) * 100}
            color={currentCount === MAX_SKILLS ? 'error' : currentCount >= 5 ? 'warning' : 'success'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </CardContent>
      </Card>

      {/* Обязательные и заблокированные навыки */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {requiredSkills.length > 0 && (
          <Grid item xs={12} md={6}>
            <Alert severity="info">
              <Typography variant="subtitle2" gutterBottom>
                Обязательные навыки шаблона:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {requiredSkills.map(skillId => {
                  const skill = skills.find(s => s.id === skillId);
                  return skill ? (
                    <Chip
                      key={skillId}
                      label={skill.name}
                      color="success"
                      size="small"
                      icon={<CheckCircleIcon />}
                    />
                  ) : null;
                })}
              </Stack>
            </Alert>
          </Grid>
        )}

        {blockedSkills.length > 0 && (
          <Grid item xs={12} md={6}>
            <Alert severity="warning">
              <Typography variant="subtitle2" gutterBottom>
                Недоступные навыки:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {blockedSkills.map(skillId => {
                  const skill = skills.find(s => s.id === skillId);
                  return skill ? (
                    <Chip
                      key={skillId}
                      label={skill.name}
                      color="error"
                      variant="outlined"
                      size="small"
                      icon={<LockIcon />}
                    />
                  ) : null;
                })}
              </Stack>
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* Выбранные навыки */}
      {currentCount > 0 && (
        <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon color="success" sx={{ mr: 1 }} />
              Выбранные навыки ({currentCount})
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {currentSkills.map(skillId => {
                const skill = skills.find(s => s.id === skillId);
                return skill ? (
                  <Tooltip key={skillId} title={skill.description}>
                    <Chip
                      label={skill.name}
                      color={isSkillRequired(skillId) ? "success" : "primary"}
                      variant="filled"
                      onDelete={isSkillRequired(skillId) ? undefined : () => handleSkillToggle(skillId)}
                      deleteIcon={isSkillRequired(skillId) ? <LockIcon /> : <CancelIcon />}
                      onClick={() => handleOpenDetails(skill)}
                      clickable
                    />
                  </Tooltip>
                ) : null;
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Навыки по категориям */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Доступные навыки по категориям
        </Typography>

        <Grid container spacing={2}>
          {Object.entries(skillCategories).map(([category, categorySkills]) => (
            <Grid item xs={12} md={6} key={category}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 1 }}>{getCategoryIcon(category)}</Box>
                    {getCategoryName(category)}
                    <Chip 
                      label={categorySkills.length} 
                      size="small" 
                      sx={{ ml: 1 }} 
                    />
                  </Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {categorySkills.map(skill => {
                      const status = getSkillStatus(skill);
                      const canSelect = canSelectSkill(skill.id);
                      
                      return (
                        <Tooltip 
                          key={skill.id} 
                          title={
                            <Box>
                              <Typography variant="subtitle2">{skill.name}</Typography>
                              <Typography variant="body2">{skill.description}</Typography>
                              {status.type === 'blocked' && (
                                <Typography variant="caption" color="error">
                                  Недоступно для вашей расы
                                </Typography>
                              )}
                            </Box>
                          }
                        >
                          <Chip
                            label={skill.name}
                            variant={status.type === 'selected' ? "filled" : "outlined"}
                            color={status.color}
                            onClick={() => handleSkillToggle(skill.id)}
                            clickable={canSelect}
                            disabled={!canSelect}
                            size="small"
                            icon={
                              status.type === 'blocked' ? <LockIcon /> :
                              status.type === 'required' ? <CheckCircleIcon /> :
                              status.type === 'selected' ? <CheckCircleIcon /> :
                              undefined
                            }
                            deleteIcon={<InfoIcon />}
                            onDelete={() => handleOpenDetails(skill)}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Предупреждение о лимите */}
      {isAtLimit && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            Достигнут лимит в {MAX_SKILLS} навыков. Чтобы выбрать новый навык, сначала отмените выбор одного из уже выбранных (кроме обязательных).
          </Typography>
        </Alert>
      )}

      {/* Диалог с деталями навыка */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        {skillDetails && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {skillDetails.name}
                <Chip 
                  label={getSkillStatus(skillDetails).label}
                  color={getSkillStatus(skillDetails).color}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                <Typography variant="body1">
                  {skillDetails.description}
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <BuildIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Категория" 
                      secondary={getCategoryName(skillDetails.category || 'other')}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ScienceIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Сложность" 
                      secondary={skillDetails.difficulty || 'Средняя'}
                    />
                  </ListItem>
                </List>

                {getSkillStatus(skillDetails).type === 'blocked' && (
                  <Alert severity="error">
                    Этот навык недоступен для выбранной расы "{selectedRace?.name}"
                  </Alert>
                )}

                {getSkillStatus(skillDetails).type === 'required' && (
                  <Alert severity="success">
                    Этот навык обязателен для выбранного шаблона "{selectedTemplate?.name}"
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Закрыть</Button>
              {getSkillStatus(skillDetails).type !== 'blocked' && (
                <Button 
                  variant="contained"
                  onClick={() => {
                    handleSkillToggle(skillDetails.id);
                    handleCloseDetails();
                  }}
                  disabled={!canSelectSkill(skillDetails.id)}
                  startIcon={isSkillSelected(skillDetails.id) ? <CancelIcon /> : <CheckCircleIcon />}
                >
                  {isSkillSelected(skillDetails.id) ? 'Отменить выбор' : 'Выбрать навык'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SkillsEditSection;